import React, { useState, useEffect } from "react";
import { clearSession } from "../shared/_helpers/session";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { getAdvisorAvailability, updateAdvisorAvailability, getOffDates, addOffDate, removeOffDate } from "../shared/_services/common.services";
import Loader from "./Loader";
import momentTz from "moment-timezone";
import {
  Button, cn, DatePicker, DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
  Field, Page, PageHead, Panel,
} from "./ui";
import StarsIcon from "../styles/StarsIcon";

const AdvisorManageAvailability = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [fromTime, setFromTime] = useState(new Date());
  const [toTime, setToTime] = useState(new Date());
  const [fromBreakTime, setFromBreakTime] = useState(new Date());
  const [toBreakTime, setToBreakTime] = useState(new Date());
  const [showDropdown, setShowDropdown] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [advisorData, setAdvisorData] = useState([]);
  const [offDates, setOffDates] = useState([]);
  const [showAvailabilityLoader, setShowAvailabilityLoader] = useState(false);
  const params = useParams();
  const [selectedDay, setSelectedDay] = useState(null);
  const [isClosed, setIsClosed] = useState(false);
  const [availabilityId, setAvailabilityId] = useState(null);
  const navigate = useNavigate();

  //get advisor availability details
  const getAdvisorData = async () => {
    setShowAvailabilityLoader(true);
    let data = await getAdvisorAvailability(params?.advisorId || JSON.parse(localStorage.getItem('currentUser'))?.user?._id);
    if (data?.status === 200 && data?.data?.availability?.length > 0) {
      setAdvisorData(data?.data?.availability[0]);
      setAvailabilityId(data?.data?.availability[0]?._id);
      const day = data?.data?.availability[0]?.days?.find(d => d.weekday === selectedDay?.weekday);
      if (day) {
        setIsClosed(day.is_closed);
      }
    }
    else {
      if (data?.response?.status === 401) {
        clearSession();
        enqueueSnackbar("Your session has been expired!", {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        navigate('/login');
      }
    }
    setShowAvailabilityLoader(false);
  };

  //get off date
  const getAdvisorOffDates = async () => {
    setShowAvailabilityLoader(true);
    let data = await getOffDates(params?.advisorId ? params?.advisorId : JSON.parse(localStorage.getItem('currentUser'))?.user?._id);
    if (data?.status === 200) {
      setOffDates([...data.data]);
    }
    setShowAvailabilityLoader(false);
  };

  useEffect(() => {
    getAdvisorData();
    getAdvisorOffDates();
  }, []);

  //add new off date
  const handleOffDateAdd = async () => {
    const advisorId = JSON.parse(localStorage.getItem('currentUser'))?.user?._id;
    const date = startDate.toISOString().split("T")[0];
    setShowAvailabilityLoader(true);
    const response = await addOffDate(advisorId, date);
    if (response?.status === 201) {
      enqueueSnackbar('Off day added successfully!', {
        variant: 'success'
      });
      getAdvisorOffDates();
      setShowDropdown(false);
      setStartDate(new Date());
    } else {
      enqueueSnackbar('Failed to add off day', {
        variant: 'error'
      });
      setShowDropdown(true);
    }
    setShowAvailabilityLoader(false);
  };

 

  //update day
  const updateDays =  async () => {
    if (fromTime >= toTime) {
      enqueueSnackbar('The "From" time cannot be the same as or later than the "To" time.', {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      return;
    }
  
    // Prepare the updated day object
    const updatedDay = {
      weekday: selectedDay.weekday, // Ensure you are updating the correct weekday
      startHour: fromTime.getHours(),
      startMinute: fromTime.getMinutes(),
      endHour: toTime.getHours(),
      endMinute: toTime.getMinutes(),
      is_closed: isClosed,
    };
  
    const response = await updateAdvisorAvailability(availabilityId, {
      day: updatedDay, 
      timezone: momentTz.tz.guess(true),
    });
  
    if (response?.status === 200) {
      getAdvisorData();
      enqueueSnackbar('Availability updated successfully!', { variant: 'success' });
    } else {
      enqueueSnackbar('Failed to update availability', { variant: 'error' });
    }
  };
  

  //add new break
  const handleAddBreak = async () => {
    if (fromBreakTime >= toBreakTime) {
      enqueueSnackbar('The "From" break time cannot be the same as or later than the "To" break time.', {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      return;
    }
  
    const newBreak = {
      startHour: fromBreakTime.getHours(),
      startMinute: fromBreakTime.getMinutes(),
      endHour: toBreakTime.getHours(),
      endMinute: toBreakTime.getMinutes()
    };
  
    const response = await updateAdvisorAvailability(availabilityId, {
      advisorId: JSON.parse(localStorage.getItem('currentUser'))?.user?._id,
      breaks: [newBreak], // Pass new break to add
      timezone: momentTz.tz.guess(true)
    });
  
    if (response?.status === 200) {
      getAdvisorData();
      enqueueSnackbar('Break added successfully!', { variant: 'success' });
    } else {
      enqueueSnackbar('Failed to add break', { variant: 'error' });
    }
  };
  

  //remove break
  const handleRemoveBreak = async (breakIndex) => {
    const breakToDelete = advisorData?.breaks[breakIndex];
  
    // Ensure that breakToDelete has the same structure as the breaks in the database
  
    const response = await updateAdvisorAvailability(availabilityId, {
      removeBreak: [breakToDelete], // Pass break to delete
      timezone: momentTz.tz.guess(true)
    });
  
    if (response?.status === 200) {
      getAdvisorData();
      enqueueSnackbar('Break deleted successfully!', { variant: 'success' });
    } else {
      enqueueSnackbar('Failed to delete break', { variant: 'error' });
    }
};


  //remove off date
  const handleRemoveOffDate = async (offDayId) => {
    setShowAvailabilityLoader(true);
    const data = await removeOffDate(offDayId);
    if (data?.status === 200) {
      enqueueSnackbar('Off day removed successfully!', { variant: 'success' });
      getAdvisorOffDates();
    } else {
      enqueueSnackbar('Failed to remove off day', { variant: 'error' });
    }
    setShowAvailabilityLoader(false);
  };


  // Function to convert 24-hour time to 12-hour time with AM/PM
  const convertTime = (hour, minute) => {
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    const minutePadded = minute.toString().padStart(2, '0');
    return `${hour12}:${minutePadded} ${ampm}`;
  };

  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const handleDayClick = (day) => {
    setSelectedDay(day);
    setFromTime(new Date(1970, 0, 1, day.startHour, day.startMinute));
    setToTime(new Date(1970, 0, 1, day.endHour, day.endMinute));
    setIsClosed(day.is_closed);
  };

  const formatOffDay = (dateString) => {
  
    const dateParts = dateString.split('-'); // ["2025", "02", "26"]
    const date = new Date(Date.UTC(dateParts[0], dateParts[1] - 1, dateParts[2])); // Ensure UTC parsing
  
    const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  
    return {
      day: date.getUTCDate(),
      month: date.toLocaleString('default', { month: 'long', timeZone: 'UTC' }), // Ensure UTC month
      year: date.getUTCFullYear(),
      weekday: weekdays[date.getUTCDay()], // Get correct weekday
    };
  };
  

  return (
    <Page>
      {showAvailabilityLoader ? <Loader /> : <div data-slot="manage-availability">
        <Button as={Link} to="/advisor-meetings" variant="icon" size="sm" iconOnly aria-label="Back" className="tw-mb-2">
          <StarsIcon name="chevronLeft" className="tw-h-4 tw-w-4 tw-shrink-0" />
        </Button>
        <PageHead title="Manage Availability" />
        <div className="tw-mb-6 tw-grid tw-grid-cols-2 tw-gap-2 md:tw-grid-cols-4 lg:tw-grid-cols-7">
          {advisorData?.days?.map((day) => (
            <button
              type="button"
              key={day.weekday}
              data-selected={selectedDay?.weekday === day.weekday ? 'true' : undefined}
              aria-pressed={selectedDay?.weekday === day.weekday}
              className={cn(
                'tw-flex tw-min-w-0 tw-cursor-pointer tw-flex-col tw-gap-1 tw-rounded-md tw-border tw-border-solid tw-p-3 tw-text-left tw-text-sm tw-text-text focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-focus-ring',
                selectedDay?.weekday === day.weekday ? 'tw-border-text tw-bg-surface-selected' : 'tw-border-divider tw-bg-surface hover:tw-bg-surface-sunken',
              )}
              onClick={() => handleDayClick(day)}
            >
              <span className="tw-font-bold">{weekdays[day.weekday]}</span>
              {day.is_closed ? (
                <span className="tw-text-text-muted">Closed</span>
              ) : (
                <span className="tw-text-text-muted">
                  {convertTime(day.startHour, day.startMinute)} to {convertTime(day.endHour, day.endMinute)}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* edit day section */}
        <div className="tw-grid tw-gap-6">
          <Panel title={selectedDay ? weekdays[selectedDay.weekday] : "Select a day"}>
            <form action="" className="tw-flex tw-flex-wrap tw-items-end tw-gap-4">
              <Field label="From" className="tw-mb-0 tw-w-40">
                <DatePicker
                  selected={fromTime}
                  onChange={(fromTime) => setFromTime(fromTime)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={30}
                  dateFormat="h:mm aa"
                />
              </Field>
              <StarsIcon name="arrow" className="tw-mb-3 tw-h-5 tw-w-5 tw-shrink-0 tw-text-text-muted max-rail:tw-hidden" />
              <Field label="To" className="tw-mb-0 tw-w-40">
                <DatePicker
                  selected={toTime}
                  onChange={(toTime) => setToTime(toTime)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={30}
                  dateFormat="h:mm aa"
                />
              </Field>
              <div className="tw-flex tw-gap-2">
                <Button id="open" variant={isClosed ? 'quiet' : 'primary'} aria-pressed={!isClosed} onClick={() => setIsClosed(false)}>Open</Button>
                <Button id="close" variant={isClosed ? 'primary' : 'quiet'} aria-pressed={isClosed} onClick={() => setIsClosed(true)}>Close</Button>
              </div>
              <Button onClick={updateDays}>Update</Button>
            </form>
          </Panel>

          <div className="tw-grid tw-gap-6 md:tw-grid-cols-2">
            {/* break hours section */}
            <Panel as="section" aria-label="Set Break Hours">
              <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-gap-2">
                <h2 className="tw-m-0 tw-text-lg tw-font-bold tw-text-text">Set Break Hours</h2>
                <Button variant="quiet" size="sm" onClick={handleAddBreak}>
                  <StarsIcon name="plus" className="tw-h-4 tw-w-4 tw-shrink-0" />
                  Add
                </Button>
              </div>
              <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-4">
                <Field label="From" className="tw-mb-0 tw-w-36">
                  <DatePicker
                    selected={fromBreakTime}
                    onChange={(fromBreakTime) => setFromBreakTime(fromBreakTime)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={30}
                    dateFormat="h:mm aa"
                  />
                </Field>
                <StarsIcon name="arrow" className="tw-mb-3 tw-h-5 tw-w-5 tw-shrink-0 tw-text-text-muted max-rail:tw-hidden" />
                <Field label="To" className="tw-mb-0 tw-w-36">
                  <DatePicker
                    selected={toBreakTime}
                    onChange={(toBreakTime) => setToBreakTime(toBreakTime)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={30}
                    dateFormat="h:mm aa"
                  />
                </Field>
              </div>
              <div className="tw-mt-4 tw-max-h-72 tw-overflow-y-auto">
                {advisorData?.breaks?.length > 0 ? (
                  advisorData?.breaks.map((key, index) => (
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-py-2 last:tw-border-b-0" key={`${key.startHour}-${key.startMinute}-${key.endHour}-${key.endMinute}`}>
                      <div className="tw-text-sm">
                        <span className="tw-font-bold">Break {index + 1} :</span>{" "}
                        <span className="tw-text-text-muted">
                          {convertTime(key.startHour, key.startMinute)} to {convertTime(key.endHour, key.endMinute)}
                        </span>
                      </div>
                      <Button variant="icon" size="sm" iconOnly onClick={() => handleRemoveBreak(index)} title="Remove Break" aria-label="Remove Break">
                        <StarsIcon name="trash" className="tw-h-4 tw-w-4 tw-shrink-0" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="tw-m-0 tw-text-sm tw-text-text-muted">No break time added</p>
                )}
              </div>
            </Panel>


            {/* off days section */}
            <Panel as="section" aria-label="Off Day">
              <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-gap-2">
                <h2 className="tw-m-0 tw-text-lg tw-font-bold tw-text-text">Off Day</h2>
                <DropdownMenu open={showDropdown} onOpenChange={(isOpen) => setShowDropdown(isOpen)}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="quiet" size="sm">
                      <StarsIcon name="plus" className="tw-h-4 tw-w-4 tw-shrink-0" />
                      Add
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="tw-w-auto tw-p-2">
                    <div onKeyDown={(e) => e.stopPropagation()}>
                      <DatePicker
                        inline
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        minDate={new Date()}
                      />
                      <div className="tw-flex tw-justify-end tw-pt-2">
                        <Button size="sm" onClick={handleOffDateAdd}>Add</Button>
                      </div>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="tw-max-h-72 tw-overflow-y-auto">
                {/* loop this offday-item */}
                {offDates.length > 0 ? (
                  offDates.map((offDate) => {
                    const formattedOffDay = formatOffDay(offDate.date);

                    return (
                      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-py-2 last:tw-border-b-0" key={offDate.id}>
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <h2 id="off_day" className="tw-m-0 tw-text-2xl tw-font-bold tw-text-text">{formattedOffDay.day}</h2>
                          <div>
                            <p id="off_day_month" className="tw-m-0 tw-text-sm tw-font-semibold tw-text-text">
                              {formattedOffDay.month} {formattedOffDay.year}
                            </p>
                            <small id="off_day_week" className="tw-text-xs tw-text-text-muted">
                              {formattedOffDay.weekday}
                            </small>
                          </div>
                        </div>
                        <Button variant="icon" size="sm" iconOnly title="Remove Off Day" aria-label="Remove Off Day" onClick={() => handleRemoveOffDate(offDate._id)}>
                          <StarsIcon name="trash" className="tw-h-4 tw-w-4 tw-shrink-0" />
                        </Button>
                      </div>
                    );
                  })
                ) : (
                  <p className="tw-m-0 tw-text-sm tw-text-text-muted">No off days added</p>
                )}
              </div>
            </Panel>
          </div>
        </div>
      </div>}
    </Page>
  );
};

export default AdvisorManageAvailability;
